import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Transition, Dialog } from "@headlessui/react";
import { visitPrints } from "../print/openPrintSizePage";

const EMPTY_REMOTE_STATE = {
  images: [],
  nextPage: null,
  totalCount: 0,
};

const ImageBrowser = ({ browser_url, images = [], inline_prints = false }) => {
  const [artworkModalOpen, setArtworkModalOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setArtworkModalOpen(true)}
        className="fixed left-0 top-1/2 z-40 rounded-b-md bg-stone-900 px-4 py-3 text-xs font-semibold uppercase tracking-widest text-white shadow-md hover:bg-stone-800 transition-colors"
        style={{
          transform: "rotate(-90deg) translateX(-50%)",
          transformOrigin: "top left",
        }}
      >
        My Uploads <i className="fa-solid fa-rectangle-pro ml-2"></i>
      </button>
      <DetailModal
        browserUrl={browser_url}
        images={images}
        inlinePrints={inline_prints}
        artworkModalOpen={artworkModalOpen}
        closeModal={() => setArtworkModalOpen(false)}
      />
    </>
  );
};

function DetailModal({
  artworkModalOpen,
  browserUrl,
  closeModal,
  images,
  inlinePrints = false,
}) {
  const searchInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [inlineFilteredImages, setInlineFilteredImages] = useState(images);
  const [remoteState, setRemoteState] = useState(EMPTY_REMOTE_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const isRemote = Boolean(browserUrl);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 250);

    return () => window.clearTimeout(timerId);
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setInlineFilteredImages(images);
      return;
    }

    const nextImages = images.filter((image) =>
      image.filename.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setInlineFilteredImages(nextImages);
  }, [images, searchTerm]);

  useEffect(() => {
    if (!artworkModalOpen || !searchInputRef.current) return;

    window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  }, [artworkModalOpen]);

  useEffect(() => {
    if (!artworkModalOpen || !isRemote) return;
    loadPage(1, debouncedSearchTerm);
  }, [artworkModalOpen, debouncedSearchTerm, isRemote]);

  const displayedImages = useMemo(() => {
    return isRemote ? remoteState.images : inlineFilteredImages;
  }, [inlineFilteredImages, isRemote, remoteState.images]);

  const hasNextPage = isRemote && remoteState.nextPage !== null;

  async function loadPage(page, query, append = false) {
    if (!browserUrl) return;

    const loadingSetter = append ? setIsLoadingMore : setIsLoading;
    loadingSetter(true);

    try {
      const response = await axios.get(browserUrl, {
        params: {
          page,
          q: query || undefined,
        },
      });
      const payload = response.data || EMPTY_REMOTE_STATE;

      setRemoteState((prev) => ({
        images: append
          ? prev.images.concat(payload.images || [])
          : payload.images || [],
        nextPage: payload.next_page ?? null,
        totalCount: payload.total_count ?? 0,
      }));
    } catch (error) {
      console.error("Failed to load uploads browser:", error);
      if (!append) setRemoteState(EMPTY_REMOTE_STATE);
    } finally {
      loadingSetter(false);
    }
  }

  const handleLoadMore = () => {
    if (!hasNextPage || isLoadingMore) return;
    loadPage(remoteState.nextPage, debouncedSearchTerm, true);
  };

  return (
    <Transition.Root show={artworkModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-stone-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-96">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-0"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-0"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4">
                      <button
                        type="button"
                        className="rounded-md text-stone-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={closeModal}
                      >
                        X
                      </button>
                    </div>
                  </Transition.Child>

                  <div className="h-full overflow-y-scroll bg-white p-8">
                    <div className="space-y-4 pb-16">
                      <div className="mb-4">
                        <input
                          type="text"
                          placeholder="Search images..."
                          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500"
                          value={searchTerm}
                          onChange={(event) =>
                            setSearchTerm(event.target.value)
                          }
                          ref={searchInputRef}
                        />
                      </div>

                      {isRemote && (
                        <div className="text-xs text-stone-500">
                          {remoteState.totalCount} uploads
                        </div>
                      )}

                      <ul
                        role="list"
                        className="grid grid-cols-1 gap-x-4 gap-y-4"
                      >
                        {displayedImages.map((image) => (
                          <li
                            key={image.id || image.image_preview}
                            className="relative flex items-center space-x-4"
                          >
                            <a
                              href={image.url}
                              className="group block w-1/3 overflow-hidden bg-stone-100"
                              onClick={(event) => {
                                if (!inlinePrints) return;
                                if (
                                  event.metaKey ||
                                  event.ctrlKey ||
                                  event.shiftKey ||
                                  event.altKey ||
                                  event.button !== 0
                                ) {
                                  return;
                                }
                                event.preventDefault();
                                visitPrints(image.url);
                              }}
                            >
                              <img
                                src={image.image_preview}
                                alt=""
                                className="pointer-events-none object-cover group-hover:opacity-75"
                                loading="lazy"
                              />
                            </a>
                            <div className="w-2/3">
                              <p className="pointer-events-none block truncate text-sm mb-1 font-medium text-stone-900">
                                {image.filename}
                              </p>
                              <p className="pointer-events-none block text-xs font-medium text-stone-500">
                                {image.dimensions}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>

                      {(isLoading || isLoadingMore) && (
                        <div className="py-6 text-center text-sm text-stone-500">
                          Loading uploads...
                        </div>
                      )}

                      {!isLoading && displayedImages.length === 0 && (
                        <div className="py-6 text-center text-sm text-stone-500">
                          No uploads found.
                        </div>
                      )}

                      {hasNextPage && !isLoading && (
                        <div className="pt-2 text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={handleLoadMore}
                          >
                            Load more
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default ImageBrowser;
